import { Portal, TemplatePortal, PortalModule } from '@angular/cdk/portal';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
    ViewContainerRef,
    inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Subscription } from 'rxjs';
import { PorIcon, PorIconModule } from '@por/shared/ui/icon-lib';
import { ApxError } from '../models/error.model';
import { ComponentSubscription } from '../utils/decorators/component-subscription';
import { ApxTabComponent } from './tab/tab.component';

export type ScrollDirection = 'left' | 'right';

// Tab is already handled by the broswer
// The standard keycode field is deprecated. We are using the "key" field instead
export enum KeyValue {
    RightArrow = 'ArrowRight',
    LeftArrow = 'ArrowLeft',
    Enter = 'Enter',
    Space = 'Space',
    Home = 'Home',
    End = 'End'
}

@Component({
    selector: 'apx-tab-group',
    standalone: true,
    imports: [CommonModule, PortalModule, PorIconModule],
    templateUrl: './tab-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApxTabGroupComponent implements AfterViewInit, OnDestroy {
    private readonly vcr = inject(ViewContainerRef);
    private readonly cdr = inject(ChangeDetectorRef);

    @ComponentSubscription subs: Subscription[] = [];
    @ContentChildren(ApxTabComponent) tabs!: QueryList<ApxTabComponent>;
    @ViewChild('tabsBox') tabsBox!: ElementRef;
    @HostBinding('class.max-w-full') maxWithFull = true;
    @Input() includeContainer = false;
    @Input() set value(value: string) {
        this.valueInternal = value;
        if (!this.tabsSet || !this.valueInternal) {
            return;
        }
        this.changeSelectedTab(value);
    }

    @Input() alignTabs: 'start' | 'center' | 'end' = 'start';
    @Output() readonly tabChanged = new EventEmitter<string>();

    selectedPortal!: Portal<unknown>;
    portals: { [tabValue: string]: TemplatePortal<unknown> } = {};
    tabValues: string[] = []; // Used as a reference to allow for the selection of tabs

    valueInternal?: string;
    tabsSet = false;

    scroll: {
        showArrows$: BehaviorSubject<boolean>;
        maxWidth: number;
        moveDistance: number;
        intervalSpeed: number;
        left: {
            handler?: ReturnType<typeof setInterval>;
            disable$: BehaviorSubject<boolean>;
            icon: PorIcon;
        };
        right: {
            handler?: ReturnType<typeof setInterval>;
            disable$: BehaviorSubject<boolean>;
            icon: PorIcon;
        };
    } = {
        showArrows$: new BehaviorSubject(false),
        maxWidth: 0,
        left: {
            disable$: new BehaviorSubject(true),
            icon: 'chevronleft'
        },
        right: {
            disable$: new BehaviorSubject(false),
            icon: 'chevronright'
        },
        moveDistance: 100,
        intervalSpeed: 100
    };

    ngAfterViewInit(): void {
        if (!this.tabs?.length) {
            throw new ApxError('No ApxTabComponent instances found. Be sure to use ApxTabComponent inside a ApxTabGroupComponent');
        }
        this.handleTabSelection();
        this.setShowScrollArrows();
    }

    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
    ngOnDestroy() {}

    handleTabSelection() {
        this.tabs.forEach(tab => {
            // Sets up template portals for each tab
            this.portals[tab.value] = new TemplatePortal(tab.content, this.vcr);
            // Creates array of tab values that are necessary for accessibilty keyboard navigation
            this.tabValues.push(tab.value);

            // Sets up subscriptions for selected tabs
            this.subs.push(
                tab.clicked.subscribe(selectedValue => {
                    this.changeSelectedTab(selectedValue);
                })
            );
        });
        const uniqueTabValues = new Set(this.tabValues);
        if (this.tabValues.length !== uniqueTabValues.size) {
            throw new ApxError('All ApxTabComponent must have unique value or title');
        }
        this.tabsSet = true;
        if (!this.valueInternal) {
            this.valueInternal = this.tabs.first.value;
        }
        this.value = this.valueInternal;
    }

    changeSelectedTab(selectedValue: string) {
        this.cdr.detach();
        this.tabs.forEach(tab => {
            tab.selected = tab.value === selectedValue;
            if (tab.selected) {
                this.selectedPortal = this.portals[tab.value];
                tab.focusOnTab();
                this.setDisabledArrows();
                this.tabChanged.emit(tab.value);
            }
        });
        this.cdr.reattach();
        this.cdr.detectChanges();
    }

    startScroll(direction: ScrollDirection) {
        this.moveTabs(direction);
        this.scroll[direction].handler = setInterval(() => {
            this.moveTabs(direction);
            if (this.scroll[direction].disable$.value) {
                this.stopScroll(direction);
            }
        }, this.scroll.intervalSpeed);
    }

    stopScroll(direction: ScrollDirection) {
        if (this.scroll[direction].handler) {
            clearInterval(this.scroll[direction].handler);
        }
    }

    moveTabs(direction: ScrollDirection) {
        if (direction === 'left') {
            this.tabsBox.nativeElement.scrollLeft -= this.scroll.moveDistance;
        } else {
            this.tabsBox.nativeElement.scrollLeft += this.scroll.moveDistance;
        }
        this.setDisabledArrows();
    }

    setDisabledArrows() {
        const disableLeft = this.tabsBox.nativeElement.scrollLeft === 0;
        const disableRight = this.tabsBox.nativeElement.scrollLeft > this.scroll.maxWidth;
        if (disableLeft !== this.scroll.left.disable$.value) {
            this.scroll.left.disable$.next(disableLeft);
        }
        if (disableRight !== this.scroll.right.disable$.value) {
            this.scroll.right.disable$.next(disableRight);
        }
    }

    @HostListener('window:resize', ['$event'])
    setShowScrollArrows() {
        const viewportWidth = this.tabsBox.nativeElement.offsetWidth;
        let childrenLength = 0;
        for (let tabIndex = 0; tabIndex < this.tabsBox.nativeElement.children.length; tabIndex++) {
            childrenLength += this.tabsBox.nativeElement.children[tabIndex].offsetWidth;
        }

        const showScrollArrows = childrenLength - 1 > viewportWidth;
        if (this.scroll.showArrows$.value !== showScrollArrows) {
            this.scroll.showArrows$.next(showScrollArrows);
        }
        this.scroll.maxWidth = childrenLength - viewportWidth;
        this.setDisabledArrows();
    }

    @HostListener('window:keyup', ['$event'])
    handleKeyboardInput(event: KeyboardEvent) {
        /*
         * Keyboard navigation for tabs is based on the WAI-ARIA pattern for tabs
         * https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
         */
        let theFocusedTab: string | false = false;
        this.tabs.forEach(tab => {
            if (tab.isFocusedOn) {
                theFocusedTab = tab.value;
            }
        });

        if (theFocusedTab === false) {
            return;
        }

        const currentFocusedTabIndex = this.tabValues.indexOf(theFocusedTab);
        const lastTabIndex = this.tabValues.length - 1;
        const firstTabValue = this.tabValues[0];
        const lastTabValue = this.tabValues[lastTabIndex];
        let updatedFocusTabValue = '';
        switch (event.code) {
            case KeyValue.RightArrow:
                updatedFocusTabValue = currentFocusedTabIndex === lastTabIndex ? firstTabValue : this.tabValues[currentFocusedTabIndex + 1];
                break;
            case KeyValue.LeftArrow:
                updatedFocusTabValue = currentFocusedTabIndex === 0 ? lastTabValue : this.tabValues[currentFocusedTabIndex - 1];
                break;
            case KeyValue.Home:
                updatedFocusTabValue = firstTabValue;
                break;
            case KeyValue.End:
                updatedFocusTabValue = lastTabValue;
                break;
            case KeyValue.Enter:
            case KeyValue.Space:
                this.changeSelectedTab(theFocusedTab);
                return;
            default:
                return;
        }

        this.tabs.forEach(tab => {
            if (tab.value === updatedFocusTabValue) {
                tab.focusOnTab();
            }
        });
    }
}
